//*************************************************************************************
//* WaMS Component Framework - app.js
//* Version 1.07f
//* Author: Damian Koch
//* Component framework essentials, plus utilities.
//* Requires jQuery, foundation
//* 4/05/22  DBK: Squish comments, added util namespace & console helpers
//* 4/18/22  DBK: Moved functionality to app.util namespace
//*************************************************************************************

//$(document).foundation();
var version = '1.1';

/**
* Represents a page in the application.
* @class
*/
class Page {

    /**
     * Creates an instance of Page.
     * @constructor
     * @param {string} [applicationPath=''] - The application path.
     */
    constructor(applicationPath) {
        app.util.console.groupCollapsed("Initializing Component Framework 1.07f...");

        /**
         * The application path.
         * @type {string}
         */
        this.appPath = applicationPath || '';

        /**
         * The ID of the page.
         * @type {number}
         */
        this.id = 0;

        /**
         * The jQuery element representing the page.
         * @type {jQuery}
         */
        this.el = $(document);

        /**
         * An object containing all components on the page.
         * @type {Object.<string, Component>}
         */
        this.components = {};

        /**
         * An object containing all component actions on the page.
         * @type {Object.<string, ComponentAction>}
         */
        this.componentActions = {};

        /**
         * The currently active component.
         * @type {Component}
         */
        this.currentComponent = {};

        /**
         * An object containing all components, including grandchild components.
         * @type {Object.<string, Component>}
         */
        this.allComponents = {};

        /**
         * An object for storing data related to the page.
         * @type {Object}
         */
        this.storage = {};

        /**
        * URLs for component actions.
        * @type {Object.<string, string>}
        */
        this.selectors = {
            components: ':not([data-role~="component-template"]) [data-role~="component"], >[data-role~="component"]',
            grandchildComponent: ':not([data-role~="component-template"]) [data-role~="component"] [data-role~="component"], > [data-role~="component"] [data-role~="component"]',
            componentActions: '[data-role~="component-action"]',
            grandchildAction: ':not([data-role~="component-template"]) [data-role~="component"] [data-role~="component-action"], > [data-role~="component"] [data-role~="component-action"]',
            datepicker: '[data-role~="datepicker"]',
            modals: {
                confirmRemove: '#removeConfirmModal',
                confirmRemoveOkButton: '#removeConfirmModal--ok',
                descriptiveError: '#descriptiveErrorModal',
                descriptiveErrorMessage: '[data-role~="descriptiveErrorMessage"]',
                loadError: '#loadErrorModal',
                removeConfirm: '#removeConfirmModal',
                saveError: '#saveErrorModal'
            },
            searchBar: '[name=searchForm]'
        };

        // Initialize checkbox lists, modals, tooltips, components, and back to top button.
        this.initModals();
        this.initTooltips();
        this.initComponents();
        this.initBackToTopButton();

        app.util.console.groupEnd();
        app.util.console.log('Initialization complete.');
    }



    /**
     * Initializes components on the page.
     * @private
     */
    initComponents() {
        var page = this;

        app.util.console.groupCollapsed('Initializing Components');

        $(this.selectors.components).not(this.selectors.grandchildComponent).each(function () {
            page.initComponent(this, page);
        });

        app.util.console.groupEnd();
        app.util.console.groupCollapsed('Initializing Component Actions');

        $(this.selectors.componentActions).not(this.selectors.grandchildAction).each(function () {
            page.initPageAction(this, page);
        });

        app.util.console.groupEnd();
    }

    /**
     * Initializes a component on the page.
     * @param {Element} e - The element representing the component.
     * @param {Page|Component} pc - The parent component or page.
     * @param {string} [subtype] - The subtype of the component.
     */
    initComponent(e, pc, subtype) {
        // Determine the type and group of the component
        var type = $(e).closest('[data-component-type]').data('component-type');
        var group = type + ($(e).closest('[data-component-group]').data('component-group') || "");
        var parent = pc || this;

        // If the component group doesn't exist yet, initialize it
        if (parent.components[group] === undefined) {
            parent.components[group] = [];
            parent.components[`${group}-noResults`] = $(`[data-component-role="noResults"][data-component-type=${group}]`, parent.el);
        }

        // Create a new component object and initialize it
        var obj = new Component(e, pc, this, subtype);
        obj.init();

        // Add the component to the parent's components array
        parent.components[group].push(obj);

        // Find all components on the page
        this.findAllComponents();

        // Trigger an event signaling that component initialization is complete
        $(document).trigger("component-init-complete", obj);
    }

    /**
     * Initializes a page action on the page.
     * @param {Element} e - The element representing the page action.
     * @param {Page|Component} pc - The parent component or page.
     */
    initPageAction(e, pc) {
        var parent = pc || this;
        var obj = new PageAction(e, pc, this);

        // If the action type doesn't exist yet, initialize it
        if (parent.componentActions[obj.type] === undefined) {
            parent.componentActions[obj.type] = [];
        }

        // Bind the action to its event
        $(e).on(obj.event, $.proxy(obj.triggerAction, obj));

        // Watch for Enter keypress on component action text boxes to prevent form submission
        $(e).on("keypress keydown", function (e) {
            if (e.key === "Enter" && $(e.target).is("input[type='text']")) {
                e.preventDefault();
                return false;
            }
        });

        // Add the action to the parent's component actions array
        parent.componentActions[obj.type].push(obj);

        // Trigger an event signaling that component action initialization is complete
        $(document).trigger("component-action-init-complete", obj);

        // Log the action bound event
        app.util.console.log(obj.action, app.util.console.successContent(obj.target), '(' + obj.event + ')', 'action bound.');
    }

    /**
     * Initializes tooltips on the page using the Foundation framework.
     */
    initTooltips() {
        $(document).foundation();
    }

    /**
     * Initializes modal functionality, such as event handlers for modal buttons.
     */
    initModals() {
        // Bind click event handler for the "Ok" button in the confirm remove modal
        $(this.selectors.modals.confirmRemoveOkButton).on('click', $.proxy(this.handleRemoveOkButton, this));
    }
    /**
     * Handler function for the "Ok" button in the remove confirmation modal.
     */
    handleRemoveOkButton() {
        // Check if there is a current component and if it has a 'confirmRemove()' method
        if (this.currentComponent && typeof this.currentComponent.confirmRemove === 'function') {
            // Call the 'confirmRemove()' method of the current component
            this.currentComponent.confirmRemove();
        }
    }
    /**
     * Initializes functionality for the back-to-top button.
     * This function shows/hides the button based on the user's scroll position and handles its click event.
     */
    initBackToTopButton() {
        // Show/hide back-to-top button based on scroll position
        $(window).scroll(function () {
            // Check if back-to-top button is fixed
            if ($(".back-to-top").css('position') === 'fixed') {
                // Get the top position of the main content area
                var mainTop = $('main').offset().top;
                // Show button if user scrolls past the main content area
                if ($(this).scrollTop() > mainTop) {
                    $('.back-to-top').fadeIn();
                } else {
                    $('.back-to-top').fadeOut();
                }
            }
        });

        // Handle click event for back-to-top button
        $(document).on('click', '.back-to-top', function () {
            // Hide the back-to-top button when clicked
            $(this).fadeOut();
        });
    }

    /**
     * Initializes options for toast notifications using the Toastr library.
     * This function sets various options such as position, duration, animation effects, etc.
     */
    initToasts() {
        // Set options for toastr notifications
        window.toastr.options = {
            "closeButton": true,
            "debug": false,
            "newestOnTop": false,
            "progressBar": true,
            "positionClass": 'toast-top-right',
            "preventDuplicates": false,
            "onclick": null,
            "showDuration": '300',
            "hideDuration": '1000',
            "timeOut": '5000',
            "extendedTimeOut": '1000',
            "showEasing": 'swing',
            "hideEasing": 'linear',
            "showMethod": 'fadeIn',
            "hideMethod": 'fadeOut'
        };
    }

    /**
     * Displays a descriptive error message in a modal.
     * @param {string} message - The error message to display.
     */
    showDescriptiveError(message) {
        // Set the error message in the modal
        $(this.selectors.modals.descriptiveErrorMessage, this.selectors.modals.descriptiveError).html(message);
        // Open the descriptive error modal
        $(this.selectors.modals.descriptiveError).foundation('open');
    }

    /**
     * Handles AJAX errors that occur during requests.
     * @param {XMLHttpRequest} xmlHttpRequest - The XMLHttpRequest object representing the AJAX request.
     * @param {string} textStatus - The status of the error.
     * @param {string} error - The error message.
     */
    handleAjaxError(xmlHttpRequest, textStatus, error) {
        // Check if the error status is unauthorized or redirected to login
        if (xmlHttpRequest.status === 401 || xmlHttpRequest.status === 302) {
            // Redirect to the login page
            window.location.replace(this.appPath + '/Account/Login?ReturnUrl=' + encodeURIComponent(window.location.pathname));
        } else if (textStatus !== 'abort' && xmlHttpRequest.readyState !== 0) {
            // Log the error and handle it
            app.util.console.error(error);
            // Check if there is a response text
            if (xmlHttpRequest.responseText !== '') {
                // Show descriptive error message if available
                app.page.showDescriptiveError(xmlHttpRequest.responseText);
            } else {
                // Open the default load error modal
                $(this.selectors.modals.loadError).foundation('open');
            }
        }
    }

    /**
     * Finds all components on the page.
     * This function resets the 'allComponents' property and then calls 'findComponents()' with the 'components' property.
     */
    findAllComponents() {
        // Reset the 'allComponents' property to an empty object
        this.allComponents = {};
        // Find components starting from the 'components' property
        this.findComponents(this.components);
    }

    /**
     * Recursively finds all components within nested component groups.
     * @param {Object} components - The components to search through.
     */
    findComponents(components) {
        // Iterate over each group of components
        for (var group in components) {
            // If the group doesn't exist in 'allComponents', initialize it as an empty array
            if (this.allComponents[group] === undefined) {
                this.allComponents[group] = [];
            }
            // Merge the components from the current group into 'allComponents'
            $.merge(this.allComponents[group], components[group]);
            // Recursively call 'findComponents' for each component within the group
            for (var key in components[group]) {
                this.findComponents(components[group][key].components);
            }
        }
    }


    /**
     * Retrieves a component by its type and ID.
     * @param {string} componentType - The type of the component to retrieve.
     * @param {string} componentId - The ID of the component to retrieve.
     * @returns {Component|null} The found component, or null if not found.
     */
    getComponent(componentType, componentId) {
        // Check if the component type exists in 'allComponents'
        if (this.allComponents[componentType]) {
            // Find the component with the matching ID within the specified type
            return this.allComponents[componentType].find((o) => o.id === componentId) || null;
        }
        // Return null if the component type does not exist
        return null;
    }

    /**
     * Refreshes the view mode of the specified component(s) based on component type and/or ID.
     * 
     * If a specific component type is provided:
     * - If a component ID is specified, it refreshes only that component of the given type.
     * - If no component ID is provided, it refreshes all components of that type.
     * 
     * If no component type is specified but a component ID is provided:
     * - It searches across all component types and refreshes any components matching the given ID.
     * 
     * Logs relevant messages to the console via the `app.util.console` utility functions.
     * 
     * @param {string} [componentType] - The type of component to refresh (e.g., "person", "vehicle"). If omitted, all types will be considered.
     * @param {number} [componentId] - The ID of the component to refresh. If omitted, all components within the specified type will be refreshed.
     */

    refreshTarget(componentType, componentId) {
        // Refresh by component type
        if (componentType) {
            const components = app.page.allComponents[componentType];
            if (!components) {
                app.util.console.alertContent(`Refresh Target: Type '${componentType}' not found.`);
                return;
            }

            if (componentId) {
                // Refresh a specific component by ID
                const target = components.find(x => x.id == componentId);
                if (target && typeof target.startViewMode === 'function') {
                    target.startViewMode();
                    app.util.console.successContent(`Refresh Target: Component with ID ${componentId} in type '${componentType}' refreshed successfully.`);
                } else {
                    app.util.console.warningContent(`Refresh Target: No component found with ID ${componentId} in type '${componentType}'.`);
                }
            } else {
                // Refresh all components of the specified type
                components.forEach(item => {
                    if (typeof item.startViewMode === 'function') {
                        item.startViewMode();
                    }
                });
                app.util.console.successContent(`Refresh Target: All components of type '${componentType}' refreshed successfully.`);
            }
        } else if (componentId) {
            // No specific type specified; handle by component ID across all types
            let found = false;
            Object.values(app.page.allComponents).forEach(group => {
                group.forEach(item => {
                    if (item.id == componentId && typeof item.startViewMode === 'function') {
                        item.startViewMode();
                        found = true;
                    }
                });
            });

            if (!found) {
                app.util.console.alertContent(`Refresh Target: No component found with ID ${componentId} across all types.`);
            } else {
                app.util.console.successContent(`Refresh Target: Component with ID ${componentId} refreshed across all types.`);
            }
        } else {
            app.util.console.calloutContent('Refresh Target: Component type and/or ID must be specified.');
        }
    }
}

/**
    * Represents a component on the page.
    * @param {Element} e - The DOM element representing the component.
    * @param {Component|Page} pc - The parent component or page.
    * @param {Page} page - The page containing the component.
    * @param {string} [subtype] - The subtype of the component.
    */
class Component {
    /**
    * Creates an instance of Component.
    * @constructor
    * @param {Element} e - The DOM element representing the component.
    * @param {Component|Page} pc - The parent component or page.
    * @param {Page} page - The page containing the component.
    * @param {string} [subtype] - The subtype of the component.
    */
    constructor(e, pc, page, subtype) {
        /**
            * DOM element representing the component.
            * @member {jQuery}
            */
        this.el = $(e);

        /**
            * Parent controller (component or page).
            * @member {Component|Page}
            */
        this.parentController = pc || {};

        /**
            * The page containing the component.
            * @member {Page}
            */
        this.page = page || app.page;

        /**
            * Subtype of the component.
            * @member {string}
            */
        this.subtype = subtype;

        /**
            * ID of the component.
            * @member {number|string}
            */
        this.id = this.el.data('component-id') || 0;

        /**
            * Role of the component.
            * @member {string}
            */
        this.role = this.el.closest('[data-component-role]').data('component-role');

        /**
            * Type of the component.
            * @member {string}
            */
        this.type = this.el.closest('[data-component-type]').data('component-type');

        /**
            * Group of the component.
            * @member {string}
            */
        this.group = this.type + (this.el.closest('[data-component-group]').data('component-group') || "");

        /**
            * State of the component.
            * @member {string}
            */
        this.state = this.el.data('component-state');

        /**
            * XMLHttpRequest object for AJAX requests.
            * @member {XMLHttpRequest|null}
            */
        this.request = null;

        /**
            * Flag indicating whether the component is being cloned.
            * @member {boolean}
            */
        this.isCloning = false;

        /**
            * Controller associated with the component.
            * @member {string}
            */
        this.controller = this.el.closest('[data-component-controller]').data('component-controller');

        /**
            * Siblings of the component within the same group.
            * @member {Array.<Component>}
            */
        this.siblings = pc ? pc.components[this.group] || [] : [];

        /**
            * Selectors related to the component.
            * @member {Object}
            */
        this.selectors = {
            // Selectors for various component elements
            editComponent: `> .card > .component-edit > [data-component-role="EditComponent"], 
                            > .card.component-edit > [data-component-role="EditComponent"], 
                            > .component-edit[data-component-role="EditComponent"]`,
            viewComponent: `> .card > .component-view > [data-component-role="ViewComponent"], 
                            > .component-view[data-component-role="ViewComponent"]`,
            buttons: {
                cancel: `> .card > .component-edit > .component-buttons [data-component-role="CancelButton"], 
                         > .card.component-edit > .component-buttons [data-component-role="CancelButton"],
                         > .component-edit > .component-buttons > [data-component-role="CancelButton"]`,
                clone: `> .card > .component-view > [data-component-role="CloneButton"]`,
                edit: `> .card > .component-view > .component-buttons > [data-component-role="EditButton"],
                       > .component-view > .row > .component-buttons > [data-component-role="EditButton"],
                       > .card > .component-view > .card-body > .row > .component-buttons > [data-component-role="EditButton"],
                       > .component-view[data-component-role="ViewComponent"] > div > [data-component-role="EditButton"]`,
                action: `> .card > .component-view > .component-buttons > [data-component-role="ActionButton"],
                         > .component-view > .row > .component-buttons > [data-component-role="ActionButton"],
                         > .card > .component-view > .card-body > .row > .component-buttons > [data-component-role="ActionButton"],
                         > .card > .component-view > .card-body > .component-collapse > .component-collapse-panel > .row > .columns > .display-value > [data-component-role="ActionButton"],
                         > .component-view[data-component-role="ViewComponent"] > div > [data-component-role="ActionButton"]`,
                remove: `> .card > .component-edit > .component-buttons [data-component-role="RemoveButton"], 
                         > .card.component-edit > .component-buttons [data-component-role="RemoveButton"],
                         > .component-edit > .component-buttons > [data-component-role="RemoveButton"]`,
                save: `> .card > .component-edit > .component-buttons [data-component-role="SaveButton"], 
                       > .card.component-edit > .component-buttons [data-component-role="SaveButton"],
                       > .component-edit > .component-buttons > [data-component-role="SaveButton"]`,
                refresh: `> .card > .component-view > .component-buttons > [data-component-role="RefreshButton"],
                         > .component-view > .row > .component-buttons > [data-component-role="RefreshButton"],
                         > .card > .component-view > .card-body > .row > .component-buttons > [data-component-role="RefreshButton"],
                         > .card > .component-view > .card-body > .component-collapse > .component-collapse-panel > .row > .columns > .display-value > [data-component-role="RefreshButton"],
                         > .component-view[data-component-role="ViewComponent"] > div > [data-component-role="RefreshButton"]`,

            },
            // Selector for the anti-forgery token used in forms
            validationToken: 'input[name="__RequestVerificationToken"]'
        };
        var controllerUrl = this.page.appPath + '/' + this.controller;
        /**
        * URLs for various actions related to the component.
        * @member {Object}
        */
        this.urls = {
            base: controllerUrl,
            view: controllerUrl + '/Show' + this.type,
            clone: controllerUrl + '/Show' + this.type + 'CloneForm',
            edit: controllerUrl + '/Show' + this.type + 'Form',
            save: controllerUrl + '/Update' + this.type,
            remove: controllerUrl + '/Remove' + this.type,
            action: controllerUrl + '/Action' + this.type,
        };

        /**
            * Sub-components associated with the component.
            * @member {Object.<string, Array.<Component>>}
            */
        this.components = {};

        /**
            * Component actions associated with the component.
            * @member {Object.<string, Array.<Component>>}
            */
        this.componentActions = {};

        /**
            * Element representing the "no results" message for the component.
            * @member {jQuery}
            */
        this.noResultsElement = (pc) ? pc.components[this.group + '-noResults'] || [] : [];

        /**
            * Views for the component in edit and view modes.
            * @member {string}
            */
        this.viewComponent = '';
        this.editComponent = '';

        /**
            * Custom data associated with the component.
            * @member {Object}
            */
        this.customRecordData = app.util.getCustomData(this.el);
    }


    /**
    * Initializes the component.
    * This method handles the initialization logic for the component,
    * including hiding the "no results" element if there are no siblings,
    * starting the view mode, or binding the view mode based on the component's state.
    */
    init() {
        /**
            * Hide the "no results" element if there are no siblings.
            * @example
            * // Hides the "no results" element if there are no siblings.
            * component.init();
            */
        if (this.siblings.length === 0 && this.noResultsElement !== undefined) {
            $(this.noResultsElement).slideUp();
        }

        /**
            * Start the view mode if the component's state is not 'active'.
            * Otherwise, bind the view mode and finish loading.
            */
        if (this.state !== 'active') {
            // Start the view mode with animation
            this.startViewMode(true);
        } else {
            // Bind the view mode and finish loading
            this.bindViewMode();
            this.finishLoad();
        }
    }

    /**
    * Starts the view mode of the component.
    * This method initiates the view mode of the component by loading data from the server,
    * applying filter criteria if provided, and handling the response.
    * @param {boolean} isInit - Indicates whether this is the initial initialization.
    * @param {Object} [filterCriteria] - Optional filter criteria to be applied when loading data.
    * @example
    * // Start the view mode with initial initialization.
    * component.startViewMode(true);
    * @example
    * // Start the view mode with filter criteria.
    * component.startViewMode(false, { filter: 'criteria' });
    */
    startViewMode(isInit, filterCriteria) {
        // Start loading animation
        this.startLoading();

        // Prepare data for AJAX request
        let data;
        if (filterCriteria) {
            data = filterCriteria;
        } else {
            data = { id: this.id, controllerId: this.parentController.id };
        }

        // Merge custom record data if available
        if (this.customRecordData) {
            data = { ...data, ...this.customRecordData };
        }

        // Merge new record data from the parent controller if available
        if (this.parentController.newRecordData) {
            data = { ...data, ...this.parentController.newRecordData };
        }

        // Abort previous request if exists
        if (this.request != null) {
            this.request.abort();
            this.request = null;
        }

        // Convert data to a query string
        const stringData = $.param(data, true);

        // Send AJAX request to retrieve component data
        this.request = $.ajax({
            url: this.urls.view,
            data: stringData,
            context: this,
            error: this.page.handleAjaxError
        })
            .done(function (data, status, xhr) {
                // Handle the response data
                const response = this.handleCFResponse(data, xhr, 'full');
                // End loading animation
                this.endLoading();
                // Bind view mode if the response is successful
                if (response.success) {
                    this.bindViewMode();
                }
                // Finish the initial load if specified
                if (isInit) {
                    this.finishLoad();
                }
            });
    }

    /**
    * Binds the view mode of the component.
    * This method sets up the view mode of the component by selecting the edit and view elements,
    * binding events, triggering events, binding sub-components, and initializing Foundation if present.
    * @example
    * // Bind the view mode of the component.
    * component.bindViewMode();
    */
    bindViewMode() {
        // Select the edit and view elements within the component
        this.editComponent = $(this.selectors.editComponent, this.el);
        this.viewComponent = $(this.selectors.viewComponent, this.el);

        // Bind events to the component elements
        this.bindByElement(this.el, false);

        // Trigger a custom event indicating completion of view mode binding
        this.triggerEvent("component-bind-view-mode-complete");

        // Bind sub-components within the view mode
        this.bindSubComponents();

        //TODO: THis should go in app.input with a trigger?
        // Initialize Foundation components within the view mode if present
        this.viewComponent.foundation();
    }

    /**
    * Initiates the refresh mode of the component.
    * This method starts the refresh mode of the component by invoking the startViewMode method.
    * @param {Event} ev - The event object associated with the trigger.
    * @example
    * // Start the refresh mode of the component.
    * component.startRefreshMode(event);
    */
    startRefreshMode(ev) {
        // Start the view mode to refresh the component
        this.startViewMode();
    }

    /**
    * Initiates the action mode of the component.
    * This method starts the action mode of the component by sending an AJAX request to the server,
    * executing the action specified by the caller button, and handling the response accordingly.
    * @param {Event} ev - The event object associated with the trigger.
    * @example
    * // Start the action mode of the component.
    * component.startActionMode(event);
    */
    startActionMode(ev) {
        // Start loading animation
        this.startLoading();

        // Prepare data for AJAX request
        let data = { id: this.id, controllerId: this.parentController.id, subtype: this.subtype };

        // Merge custom record data if available
        if (this.customRecordData) {
            data = { ...data, ...this.customRecordData };
        }

        // Merge new record data from the parent controller if available
        if (this.id === 0 && this.parentController.newRecordData) {
            data = { ...data, ...this.parentController.newRecordData };
        }

        // Determine the action URL based on the caller button
        let actionURL = this.urls.action;
        const callerButton = $(ev.target);
        if (callerButton.data('component-action')) {
            const controller = callerButton.closest('[data-component-controller]').data('component-controller');
            const action = callerButton.data('component-action');
            actionURL = `${this.page.appPath}/${controller}/${action}`;
        }

        // Retrieve custom data associated with the caller button
        const actionCustomData = app.util.getCustomData(callerButton);
        if (actionCustomData) {
            data = { ...data, ...actionCustomData };
        }

        // Send AJAX request to execute the action
        $.ajax({
            url: actionURL,
            data: data,
            context: this,
            error: this.page.handleAjaxError
        })
            .done(function (data, status, xhr) {
                // Handle the response data
                const jsonObject = this.handleCFResponse(data, xhr, 'full');
                // End loading animation
                this.endLoading();
                // Bind view mode if the response is successful
                if (jsonObject.success) {
                    this.bindViewMode();
                    // Trigger a custom event indicating completion of the action
                    this.triggerEvent("component-action-complete");
                }
            });
    }

    /**
    * Initiates the clone mode of the component.
    * This method sets the component's cloning flag to true and starts the edit mode.
    * @example
    * // Start the clone mode of the component.
    * component.startCloneMode();
    */
    startCloneMode() {
        // Set the component's cloning flag to true
        this.isCloning = true;
        // Start the edit mode
        this.startEditMode();
    }

    /**
    * Initiates the edit mode of the component.
    * This method starts the edit mode of the component by sending an AJAX request to the server,
    * retrieving the edit HTML, and binding the edit mode accordingly.
    */
    startEditMode() {
        // Start loading animation
        this.startLoading();

        // Prepare data for AJAX request
        let data = {
            id: this.id,
            controllerId: this.parentController.id,
            subtype: this.subtype
        };

        // Merge custom record data if available
        if (this.customRecordData) {
            data = { ...data, ...this.customRecordData };
        }

        // Merge new record data from the parent controller if available
        if (this.id === 0 && this.parentController.newRecordData) {
            data = { ...data, ...this.parentController.newRecordData };
        }

        // Send AJAX request to retrieve edit HTML
        $.ajax({
            url: this.urls.edit,
            data: data,
            context: this,
            error: this.page.handleAjaxError
        })
            .done(function (data, status, xhr) {
                // Handle the response data
                const jsonObject = this.handleCFResponse(data, xhr, 'edit');

                // Set ID field value to 0 if cloning
                if (this.isCloning) {
                    $('[name="Id"]', this.editComponent).val(0);
                }

                // Add 'edit' class to the component element
                this.el.addClass('edit');

                // End loading animation
                this.endLoading();

                // Bind the edit mode
                this.bindEditMode();
            });
    }

    /**
    * Binds the edit mode of the component.
    * This method sets up event listeners for the edit mode, such as handling key presses and button clicks,
    * and triggers a custom event indicating completion of edit mode binding.
    */
    bindEditMode() {
        // Handle key presses on a form
        $('form', this.editComponent).on('keydown', $.proxy(function (e) {
            switch (e.key) {
                case "Enter":
                    // Stop default form submission on "Enter" key press in textbox
                    if ($(e.target).is("input[type='text']")) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    break;
                case "Escape":
                    e.preventDefault();
                    e.stopPropagation();
                    //this.cancelEditMode();
                    break;
            }
        }, this));
        
        // Add event listeners for cancel, save, and remove buttons
        $(this.selectors.buttons.cancel, this.el).off('click').on('click', $.proxy(this.cancelEditMode, this));
        $(this.selectors.buttons.save, this.el).off('click').on('click', $.proxy(this.saveEditMode, this));
        $(this.selectors.buttons.remove, this.el).off('click').on('click', $.proxy(this.startRemove, this));

        // Trigger a custom event indicating completion of edit mode binding
        this.triggerEvent("component-bind-edit-mode-complete");

        // Bind sub-components within the edit mode
        this.bindSubComponents();
    }

    /**
    * Cancels the edit mode of the component.
    * This method cancels the edit mode of the component by removing the 'edit' class from the component element
    * and triggering a custom event to indicate that the edit mode has been canceled.
    */
    cancelEditMode() {
        // Reset the cloning flag if applicable
        if (this.isCloning) {
            this.isCloning = false;
        }

        // If the component is a new record (ID === 0), remove it; otherwise, remove the 'edit' class
        if (this.id === 0) {
            this.removeComponent();
        } else {
            this.el.removeClass('edit');
        }

        // Trigger a custom event to indicate that the edit mode has been canceled
        this.triggerEvent("component-edit-cancel");
    }

    /**
    * Saves the changes made in edit mode of the component.
    * This method validates the form, sends an AJAX request to save the changes,
    * and handles the response accordingly.
    * @param {Event} event - The event object associated with the trigger.
    */
    saveEditMode(event) {
        // Find the form element within the edit component
        let form = $('form:not(.component-exclude)', this.editComponent);
        if (!form || form.length !== 1) {
            app.util.console.log("child form not found.");
            if (this.editComponent.is("form:not(.component-exclude)")) {
                app.util.console.log("edit component is the form");
                form = this.editComponent;
            }
        }

        // Get the caller button
        const callerButton = $(event.currentTarget);

        // Function to execute after pending requests are complete
        function proceedWithSave() {
            // Log whether the form is valid
            app.util.console.log("form valid?", form.valid());
            if (form.valid() || callerButton.data('component-ignore-validation') == true) {
                // If the top of the component is off of the page, scroll to it
                const componentTop = this.editComponent.offset().top - 50;
                if ($(document).scrollTop() > componentTop) $('html, body').scrollTop(componentTop);

                // Start loading animation
                this.startLoading();

                // Get form data
                let formData = app.util.getFormValues(form);

                // Merge action custom data if available
                const actionCustomData = app.util.getCustomData(callerButton);
                if (actionCustomData) {
                    formData = { ...formData, ...actionCustomData };
                }

                // Send AJAX request to save changes
                $.ajax({
                    url: this.urls.save,
                    method: 'post',
                    data: formData,
                    context: this,
                    error: this.page.handleAjaxError
                })
                    .done(function (data, status, xhr) {
                        // End loading animation
                        this.endLoading();

                        // Handle the response data
                        if (data) {
                            const jsonObject = this.handleCFResponse(data, xhr, 'view');
                            if (jsonObject.success) {
                                // Update component ID if it's a new record or cloning
                                if (this.id === 0 || this.isCloning) {
                                    this.id = parseInt($('[name="Id"]', this.viewComponent).val());
                                    if (this.isCloning) {
                                        this.isCloning = false;
                                    }
                                    this.bindByElement(this.viewComponent, false);
                                } else {
                                    this.bindByElement(this.viewComponent, true);
                                }

                                // Reinitialize Foundation for view component
                                this.viewComponent.foundation();

                                // Bind sub-components
                                this.bindSubComponents();

                                // Trigger a custom event indicating completion of save operation
                                this.triggerEvent("component-save-complete");

                                // Cancel edit mode
                                this.cancelEditMode();
                            }
                        } else {
                            // Open save error modal if data is empty
                            $(app.page.selectors.modals.saveError).foundation('open');
                        }
                    });
            } else {
                // Give focus to the first error
                const firstError = $('#' + $('.field-validation-error', form).data('valmsg-for'));
                if (firstError.length) {
                    $('html, body').animate({
                        scrollTop: firstError.offset().top - 50
                    }, 500);
                }
            }
        }

        if (!callerButton.data('component-ignore-validation') == true) {
            // Wait for pending requests to complete before proceeding with save
            app.util.waitForPendingRequests(form, proceedWithSave.bind(this));
        } else {
            proceedWithSave.bind(this)();
        }

    }

    /**
    * Initiates the removal process of the component.
    * This method checks if the component is a new record or an existing record,
    * and either directly removes the component (if it's a new record) or opens a confirmation modal for removal.
    */
    startRemove() {
        // If the component is a new record, directly remove it
        if (this.id === 0) {
            this.removeComponent();
        } else {
            // If the removal URL is defined and not empty, open the confirmation modal
            if (this.urls.remove !== undefined && this.urls.remove !== '') {
                // Set the current component in the page context
                app.page.currentComponent = this;
                // Open the remove confirmation modal
                $(app.page.selectors.modals.removeConfirm).foundation('open');
            }
        }
    }

    /**
    * Confirms the removal of the component.
    * This method sends an AJAX request to the server to remove the component,
    * and handles the response accordingly.
    */
    confirmRemove() {
        // If the removal URL is defined and not empty, proceed with removal
        if (this.urls.remove !== undefined && this.urls.remove !== '') {
            // Remove 'edit' class from the component element
            this.el.removeClass('edit');
            // Start loading animation
            this.startLoading();

            // Prepare data for AJAX request
            let data = { id: this.id, controllerId: this.parentController.id, __RequestVerificationToken: $(this.selectors.validationToken, this.editComponent).val() };

            // Merge custom record data if available
            if (this.customRecordData) {
                data = { ...data, ...this.customRecordData };
            }

            // Send AJAX request to remove the component
            $.ajax({
                url: this.urls.remove,
                method: 'post',
                data: data,
                context: this,
                error: this.page.handleAjaxError
            })
                .done(function (data, status, xhr) {
                    // Handle the response data
                    if (data) {
                        const jsonObject = this.handleCFResponse(data, xhr);
                        if (jsonObject.success) {
                            // Remove the component if removal is successful
                            this.removeComponent();
                            // Trigger a custom event indicating completion of removal
                            this.triggerEvent("component-remove-complete");
                        } else {
                            // Add 'edit' class back to the component element
                            this.el.addClass('edit');
                            // End loading animation
                            this.endLoading();
                        }
                    } else {
                        // Add 'edit' class back to the component element
                        this.el.addClass('edit');
                        // End loading animation
                        this.endLoading();
                        // Open save error modal if data is empty
                        $('#saveErrorModal').foundation('open');
                    }
                });
        }
    }

    /**
    * Removes the component from its parent container.
    * This method removes the component from its parent container and updates the display if necessary.
    */
    removeComponent() {
        // Find the index of the component in its siblings array
        const index = this.siblings.indexOf(this);

        // If there's only one sibling, show the 'no results' element
        if (this.siblings.length <= 1) {
            $(this.noResultsElement).slideDown();
        }

        // Remove the component from its siblings array
        this.siblings.splice(index, 1);

        // Remove the component element from the DOM
        if (this.role === 'accordion') {
            // If the component is part of an accordion, remove the accordion item
            this.el.closest('li.accordion-item').remove();
        } else {
            // Otherwise, remove the component element directly
            this.el.remove();
        }
    }


    /**
    * Binds event handlers to buttons within the specified element.
    * This method binds event handlers to buttons for various actions such as edit, refresh, action, clone, cancel, save, and remove.
    * @param {jQuery} el - The jQuery element to which event handlers will be bound.
    * @param {boolean} isRebind - Indicates whether this is a rebind operation.
    */
    bindByElement(el, isRebind) {
        // Bind event handler for edit button
        $(this.selectors.buttons.edit, this.el).off('click').on('click', $.proxy(this.startEditMode, this));

        // Bind event handler for refresh button
        $(this.selectors.buttons.refresh, this.el).off('click').on('click', $.proxy(this.startRefreshMode, this));

        // Bind event handler for action button
        $(this.selectors.buttons.action, this.el).off('click').on('click', $.proxy(this.startActionMode, this));

        // Bind event handler for clone button
        $(this.selectors.buttons.clone, this.el).off('click').on('click', $.proxy(this.startCloneMode, this));

        // Bind event handler for cancel button
        $(this.selectors.buttons.cancel, this.el).off('click').on('click', $.proxy(this.cancelEditMode, this));

        // Bind event handler for save button
        $(this.selectors.buttons.save, this.el).off('click').on('click', $.proxy(this.saveEditMode, this));

        // Bind event handler for remove button
        $(this.selectors.buttons.remove, this.el).off('click').on('click', $.proxy(this.startRemove, this));

        // Check if this is not a rebind operation
        if (!isRebind) {
            // Any additional setup if needed when not in rebind mode
        }
    }

    /**
    * Binds sub-components within the component.
    * This method initializes and binds sub-components and component actions within the current component.
    */
    bindSubComponents() {
        // Store a reference to the current controller
        const controller = this;

        // Reset the components object
        this.components = {};

        // Initialize and bind sub-components
        $(this.page.selectors.components, this.el).not($(this.page.selectors.grandchildComponent, this.el)).each(function () {
            controller.page.initComponent(this, controller);
        });

        // Initialize and bind component actions
        $(this.page.selectors.componentActions, this.el).not($(this.page.selectors.grandchildAction, this.el)).each(function () {
            controller.page.initPageAction(this, controller);
        });
    }


    /**
    * Initiates the loading state for the component.
    * This method adds a class to the component element to indicate loading state and triggers a custom event.
    */
    startLoading() {
        // Add a class to the component element to indicate loading state
        this.el.addClass('component-loading');

        // Trigger a custom event to indicate the start of loading
        this.triggerEvent("component-loading-start");
    }


    /**
    * Ends the loading state for the component.
    * This method removes the loading class from the component element and triggers a custom event.
    */
    endLoading() {
        // Remove the loading class from the component element
        this.el.removeClass('component-loading');

        // Trigger a custom event to indicate the completion of loading
        this.triggerEvent("component-loading-complete");
    }

    /**
    * Finishes the loading process for the component.
    * This method performs any necessary actions after the component finishes loading.
    */
    finishLoad() {
        // TODO: Determine the purpose of this method and document it accordingly.

        // If the component is part of an accordion, reinitialize Foundation for the accordion
        if (this.role === 'accordion') {
            window.Foundation.reInit($('.accordion'));
        }

        // If the component is in editing mode, start the edit mode
        if (this.isEditing()) {
            this.startEditMode();
        }

        // Log a message indicating successful loading
        app.util.console.log(
            app.util.console.calloutContent(this.type),
            app.util.console.successContent(this.id),
            'loaded.'
        );
    }

    /**
     * Handles the response received from a Component Framework request.
     * This method processes the response data and performs corresponding actions based on the response.
     * @param {any} data - The response data received from the request.
     * @param {XMLHttpRequest} xhr - The XMLHttpRequest object associated with the request.
     * @param {string} componentHtmlType - The type of the html passed if the response is not JSON.
     * @returns {Object} - The processed response object.
     */
    handleCFResponse(data, xhr, componentHtmlType) {
        var response;
        var contentType = xhr.getResponseHeader("Content-Type");
        if (data === true || data === false) {
            response = {
                success: data
            };
        }
        else if (contentType.includes('application/json')) {
            // Handle JSON response
            try {
                response = $.parseJSON(data);
            }
            catch {
                //response was already parsed
                response = data;
            }

        } else {
            // Handle raw response
            response = {
                success: true
            };
            if (componentHtmlType) {
                response.componentHtmlType = componentHtmlType;
                response.componentHtml = data
            }
        }

        // Display toast messages if available
        if (response.toastMessages && response.toastMessages.length > 0) {
            response.toastMessages.forEach(function (toastMessage) {
                // Display toast message using toastr.js
                var options = {};
                if (toastMessage.timeOut) options.timeOut = toastMessage.timeOut;
                if (toastMessage.extendedTimeOut) options.extendedTimeOut = toastMessage.extendedTimeOut;
                if (toastMessage.closeButton) options.closeButton = toastMessage.closeButton;
                
                toastr[toastMessage.toastType.toString().toLowerCase()](toastMessage.message, toastMessage.title,options);
                
            });
        }

        // Perform actions based on response data

        // Check if response.refreshTargets is truthy and iterate if so
        response.refreshTargets?.forEach(target => app.page
            .refreshTarget(target.componentType, target.componentId));

        if (response.componentHtml) {
            if (response.componentHtmlType === 'view') {
                this.viewComponent.html(response.componentHtml);
            } else if (response.componentHtmlType === 'edit') {
                this.editComponent.html(response.componentHtml);
            } else if (response.componentHtmlType === 'full') {
                this.el.html(response.componentHtml);
            }   
        }

        if (response.errorMessage) {
            // Show descriptive error message
            app.page.showDescriptiveError(response.errorMessage);
        }

        return response;
    }


    /**
     * Triggers a custom event and logs related information.
     * This method triggers the specified event and logs information about the event.
     * @param {string} eventName - The name of the event to trigger.
     */
    triggerEvent(eventName) {
        // Log information about the triggered event
        app.util.console.groupCollapsed(
            app.util.console.calloutContent(this.type),
            app.util.console.successContent(this.id),
            eventName,
            "triggered."
        );
        app.util.console.trace(eventName, "triggered.");
        app.util.console.trace(eventName + "-" + this.type, "triggered.");
        app.util.console.groupEnd();

        // Trigger the event
        $(document).trigger(eventName, this);
        $(document).trigger(eventName + "-" + this.type, this);
    }


    /**
     * Checks if the component is in editing mode.
     * @returns {boolean} - True if the component is in editing mode, otherwise false.
     */
    isEditing() {
        // Check if the component element has the 'edit' class
        return this.el.hasClass('edit');
    }

}

/**
 * Represents an action associated with a page component.
 * @class
 */
class PageAction {
    /**
     * Creates an instance of PageAction.
     * @param {Element|string} e - The DOM element or selector associated with the action.
     * @param {Object} [pc={}] - The parent controller object.
     * @param {Object} [page=app.page] - The page object.
     */
    constructor(e, pc, page) {
        this.el = $(e);
        this.parentController = pc || {};
        this.page = page || app.page;
        this.action = this.el.closest('[data-component-action]').data('component-action');
        this.event = this.el.closest('[data-component-action-event]').data('component-action-event') || 'click';
        this.type = this.el.closest('[data-component-type]').data('component-type');
        // Thing you are targeting (unique type (points to one))
        this.target = this.el.closest('[data-component-action-target]').data('component-action-target');
        // Type of the thing you are targeting (PersonComponents)
        this.targetType = this.el.closest('[data-component-action-target-type]').data('component-action-target-type');
        this.subtype = this.el.closest('[data-component-subtype]').data('component-subtype');

        switch (this.action) {
            case "filter":
                this.filterField = this.el.closest('[data-component-action-field]').data('component-action-field');
                break;
            case "setvalue":
                this.value = this.el.closest('[data-component-action-value]').data('component-action-value');
                break;
        }
    }
    /**
     * Triggers the action associated with the page component.
     * @param {Event} e - The event object.
     */
    triggerAction(e) {
        app.util.console.log('component action', app.util.console.calloutContent(this.action), 'for', app.util.console.warningContent(this.target), 'triggered.');

        switch (this.action) {
            case "add5":
                this.pageComponentActionAdd(this.el, this.parentController, this.target);
                this.pageComponentActionAdd(this.el, this.parentController, this.target);
                this.pageComponentActionAdd(this.el, this.parentController, this.target);
                this.pageComponentActionAdd(this.el, this.parentController, this.target);
                this.pageComponentActionAdd(this.el, this.parentController, this.target);
                break;
            case "add":
                this.pageComponentActionAdd(this.el, this.parentController, this.target);
                break;
            case "filter":
                this.pageComponentActionFilter(this.el, this.target);
                break;
            case "filterserver":
                this.pageComponentActionFilterServer(this.el, this.target, e);
                break;
            case "refresh":
                this.pageComponentActionRefresh(this.el, this.target);
                break;
            case "setoptions":
                this.pageComponentActionUpdateSetOptions(this.el, this.target);
                break;
            case "setvalue":
                this.pageComponentActionUpdateSetValue(this.el, this.target);
                break;
            case "setvisibility":
                this.pageComponentActionUpdateSetVisibility(this.el, this.target);
                break;
            case "updateid":
                this.pageComponentActionUpdateId(this.el, this.target);
                break;
            case "updatesubtype":
                this.pageComponentActionUpdateSubtype(this.el, this.target);
                break;
            case "download":
                this.pageComponentActionDownload(this.el, this.target, e);
                break;
        }

        $(document).trigger("component-action-complete", this);
        $(document).trigger("component-action-complete-" + this.type, this);
    }
    /**
     * Adds new components to the page.
     * @param {jQuery} el - The jQuery element triggering the action.
     * @param {Object} pc - The parent controller object.
     * @param {string} t - The target component.
     */
    pageComponentActionAdd(el, pc, t) {
        var target = $('[data-role~="component-action-target"][data-component-target="' + t + '"]', pc.el);
        var componentWrapper;

        if (el.data('component-template') && $('[data-role~="component-template"][data-template-name="' + el.data('component-template') + '"]')) {
            componentWrapper = $('[data-role~="component-template"][data-template-name="' + el.data('component-template') + '"]').html();
        } else {
            var wrapperType = el.data('component-wrapper-type') ? el.data('component-wrapper-type') : 'div';
            componentWrapper = '<' + wrapperType + ' class="edit ' + (el.data('component-wrapper-class') ? el.data('component-wrapper-class') : "") + '" data-role="component" data-component-id="0"></' + wrapperType + '>';
        }

        if (target.data('component-new-key') && target.data('component-new-value') !== 'undefined') {
            var count = 2;
            pc.newRecordData = {};

            pc.newRecordData[target.data('component-new-key')] = target.data('component-new-value');

            while (target.data('component-new-key' + count) && target.data('component-new-value' + count)) {
                pc.newRecordData[target.data('component-new-key' + count)] = target.data('component-new-value' + count);
                count++;
            }
        }

        if (el.data('parent-id')) {
            pc.id = el.data('parent-id');
        }
        var newComponentSelector;
        switch (target.data('component-insert-type')) {
            case 'append':
                target.append(componentWrapper);
                newComponentSelector = $('[data-role~="component"]:last', target);
                break;
            case 'sibling-append':
                target.after(componentWrapper);
                newComponentSelector = target.next();
                break;
            case 'sibling-prepend':
                target.before(componentWrapper);
                newComponentSelector = target.prev();
                break;
            default:
                target.prepend(componentWrapper);
                newComponentSelector = $('[data-role~="component"]:first', target);
                break;
        }
        if (this.el.data('component-subtype')) {
            this.subtype = this.el.data('component-subtype');
        } else {
            this.subtype = this.el.closest('[data-component-subtype]').data('component-subtype');
        }

        this.page.initComponent(newComponentSelector, pc, this.subtype);
        $(document).trigger("component-add-complete", newComponentSelector);
        $(componentWrapper).foundation();
    }
    /**
     * Filters components based on user input.
     * @param {jQuery} el - The jQuery element triggering the action.
     * @param {string} t - The target component.
     */
    pageComponentActionFilter(el, t) {

        var context = $('[data-role~="component-action-target"][data-component-target~="' + this.target + '"]');
        var rows = $('[data-role~="component"]', context);
        var form = $('form[data-role~="filter-server-form"][data-component-action-target-type~="' + this.targetType + '"]');
        rows.show();

        $(':input', form).each(function (i, e) {
            var el = $(e);
            if (el.val() !== '') {
                var field;

                if (el.data('target')) {
                    field = '[data-label="' + el.data('target') + '"]';
                }
                else {
                    field = '[data-label]';
                }

                var values = el.val().toUpperCase().split(/\s+/);
                rows.each(function (i, e) {
                    var hide = false;

                    $.each(values, function (index, v) {
                        if ($(field + ':icontains("' + v + '")', e).length < 1) {
                            hide = true;
                            return;
                        }
                    });
                    if (hide) {
                        $(e).hide();
                    }
                });
            }
        });

    }

    /**
     * Filters components on the server side based on user input.
     * 
     * This method triggers a server-side filtering operation based on user input. It gathers filter criteria from form fields, constructs a data payload, and sends an AJAX request to the server for filtering. Upon receiving the filtered data, it updates the page components accordingly.
     * 
     * @param {jQuery} el - The jQuery element triggering the action.
     * @param {string} t - The target component.
     * @param {Event} e - The event object.
     */
    pageComponentActionFilterServer(el, t, e) {

        if (e === undefined || e.type !== "keyup" || app.util.isPrintableCharacter(e)) {
            //if keyup, add a delay
            var delay = 300;
            if (e !== undefined && e.type !== "keyup") {
                delay = 0;
            }

            if (this.timeout !== null) { clearTimeout(this.timeout); }
            var that = this;
            this.timeout = setTimeout(function () {

                var form = $('form[data-role~="filter-server-form"][data-component-action-target-type="' + that.targetType + '"]');

                var dataRoles = el.data('role');
                if (dataRoles.indexOf('page-size') !== -1) {

                    //append to form
                    if (form.find('[name="pageSize"]').length) {
                        form.find('[name="pageSize"]').val(el.val());
                    } else {
                        //add to form
                        $('<input>').attr({
                            type: 'hidden',
                            id: 'pageSize',
                            name: 'pageSize',
                            value: el.val()
                        }).appendTo(form);
                    }
                }

                var filters = app.util.getFormValues(form); //form.serializeArray();

                if (el.data('page-value')) {
                    filters.pageNumber = el.data('page-value');
                }

                var target = that.page.components[that.targetType][0];
                target.startViewMode(false, filters);

            }, delay, el, that);
        }
    }

    /**
     * Refreshes components based on user input.
     * 
     * This method refreshes components based on filter criteria provided by the user. It retrieves filter values from form fields, constructs a filter object, and applies the filters to the components. It then triggers a view mode refresh for the affected components.
     * 
     * @param {jQuery} el - The jQuery element triggering the action.
     * @param {string} t - The target component.
     */
    pageComponentActionRefresh(el, t) {
        var target = this.page.components[t][0];
        if (target) {
            target.startViewMode();
        }
    }
    /**
     * Updates the ID of a component.
     * 
     * This method updates the ID of a component based on user input or predefined value. It retrieves the new ID value from the triggering element or a data attribute, applies the ID update to the component, and triggers a view mode refresh.
     * 
     * @param {jQuery} el - The jQuery element triggering the action.
     * @param {string} t - The target component.
     */
    pageComponentActionUpdateId(el, t) {
        var target = this.page.components[t][0];
        var val;
        if (el.data('component-action-updateid-value')) {
            val = el.data('component-action-updateid-value');
        } else {
            val = el.val();
        }
        if (val.length > 0) {
            target.id = val;
            target.startViewMode();
        }
    }

    /**
     * Updates the subtype of a component.
     * 
     * This method updates the subtype of a component based on user input. It retrieves the new subtype value from the triggering element and applies the subtype update to the target component.
     * 
     * @param {jQuery} el - The jQuery element triggering the action.
     * @param {string} t - The target component.
     */
    pageComponentActionUpdateSubtype(el, t) {
        var target = $('[data-role~="component-action-target"][data-component-target="' + t + '"]');
        target.data('component-subtype', el.val());
    }
    /**
     * Sets options for components based on user input.
     * 
     * This method sets options for components based on user selection. It retrieves selected options from form fields, determines the action type (e.g., show/hide), and applies the corresponding action to the target components.
     * 
     * @param {jQuery} el - The jQuery element triggering the action.
     * @param {string} t - The target component.
     * @param {Event} e - The event object.
     */
    pageComponentActionUpdateSetOptions(el, t, e) {
        var targets = $('[data-component-target="' + t + '"]');
        var values = $('input:checked', el);
        targets.each(function (i, e) {
            var targetField = $(e).data('component-action-setoptions-field');

            switch ($(e).data('component-action-setoptions-type')) {
                case "showequal":
                    //remove all options
                    if (values.length === 0 || values[0].value === '0') {
                        $('.select-many-checkbox', e).show();
                    } else {
                        $('.select-many-checkbox', e).hide();
                        $('.select-many-checkbox', e).prop('checked', false).trigger("change");

                        values.each(function (i2, e2) {
                            var value = $(e2).val();
                            //re add for thisv value
                            $('.select-many-checkbox', e).each(function () {
                                if ($('input', this).attr(targetField) === value) {
                                    $(this).show();
                                }
                            });
                        });
                    }
                    break;
            }
        });
    }
    /**
     * Sets values for components based on user input.
     * 
     * This method sets values for components based on user selection. It retrieves the value to be set from the triggering element or a data attribute and applies the value to the target component.
     * 
     * @param {jQuery} el - The jQuery element triggering the action.
     * @param {string} t - The target component.
     */
    pageComponentActionUpdateSetValue(el, t) {
        var target = $(t);
        target.val($(el).data("component-action-value"));
    }
    /**
     * Sets the visibility of components based on user input.
     * 
     * This method sets the visibility of components based on user selection. It retrieves the visibility criteria from form fields or data attributes, determines the action type (e.g., show/hide), and applies the corresponding action to the target components.
     * 
     * @param {jQuery} el - The jQuery element triggering the action.
     * @param {string} t - The target component.
     */
    pageComponentActionUpdateSetVisibility(el, t) {
        var targets = $('[data-component-target="' + t + '"]');
        var val = app.util.getElementValue(el); // Retrieve the value of the element
        targets.each(function (i, e) {
            var showTarget = null;
            var type = $(e).data('component-action-setvisibility-type');
            var targetValue = $(e).data('component-action-setvisibility-value');

            switch (type) {
                case "showchecked":
                    showTarget = el.prop("checked"); // Boolean value based on checkbox state
                    break;
                case "showunchecked":
                    showTarget = !el.prop("checked"); // Inverse of checkbox state
                    break;
                case "showequal":
                    showTarget = (val === targetValue); // Check if the values are equal
                    break;
                case "shownotequal":
                    showTarget = (val !== targetValue); // Check if the values are not equal
                    break;
                case "showcontains":
                    var vals = targetValue.split(','); // Split the target values into an array
                    showTarget = vals.includes(val); // Check if the value is in the list
                    break;
                case "shownotcontains":
                    var vals = targetValue.split(','); // Split the target values into an array
                    showTarget = !vals.includes(val); // Check if the value is not in the list
                    break;
            }
            if (showTarget != null) {
                app.util.setVisibility(e, showTarget); // Set the visibility based on the condition
            }
        });
    }



    /**
     * Initiates a download action for components.
     * 
     * This method initiates a download action for components based on user interaction. It retrieves the download URL from the triggering element or a data attribute, gathers additional data from form fields, constructs a data payload, and sends an AJAX request to the server for downloading. Upon completion, it displays a success or failure message to the user.
     * 
     * @param {jQuery} el - The jQuery element triggering the action.
     * @param {string} t - The target component.
     * @param {Event} e - The event object.
     */
    pageComponentActionDownload(el, t, e) {
        var displayName = el.data('component-displayname');
        var toastMessage = `Download in Progress${displayName ? ` (${displayName})` : ''}`;

        var toast = toastr.info(toastMessage, '', {
            closeButton: true,
            timeOut: 0,
            extendedTimeOut: 0,
        });

        var href = el.data('component-href');

        var data = {};
        if (this.target) {
            var form = $(`form[data-role~="component-action-target"][data-component-target~="${this.target}"]`);
            var formdata = app.util.getFormValues(form);
            data = { ...data, ...formdata }
        } else if (this.targetType) {
            var form = $(`form[data-component-action-target-type~="${this.targetType}"]`);
            var formdata = app.util.getFormValues(form);
            data = { ...data, ...formdata }
        }

        var actionCustomData = app.util.getCustomData(el);
        if (actionCustomData) {
            data = { ...data, ...actionCustomData };
        }

        $.ajax({
            url: href,
            method: 'POST',
            data: data,
            xhrFields: {
                responseType: 'blob'
            }
        })
            .done(function (data, status, xhr) {
                if (data.size <= 0) {
                    toastr.warning(`No data found ${displayName ? ` (${displayName})` : ''}`);
                }
                else {
                    var filename = '';
                    var disposition = xhr.getResponseHeader('Content-Disposition');
                    if (disposition && disposition.indexOf('attachment') !== -1) {
                        var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                        var matches = filenameRegex.exec(disposition);
                        if (matches != null && matches[1]) {
                            filename = matches[1].replace(/['"]/g, '');
                        }
                    }
                    var url = window.URL.createObjectURL(data);
                    var link = document.createElement('a');
                    link.href = url;
                    link.download = filename || 'default-filename';
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toastr.success(`Download Complete${displayName ? ` (${displayName})` : ''}`);
                }
            }).fail(function (jqXHR, textStatus, errorThrown) {
                toastr.error(`Download Failed${displayName ? ` (${displayName})` : ''}`);
            }).always(function () {
                toastr.clear(toast);
            });
    }

}

var app = {}
app.util = {
    console: (function () {
        "use strict";

        var queue = [];
        var token = {};

        function calloutContent(value) { return customContent(value, "display: inline-block; background-color: #0479a8; color: white; font-weight: bold; padding: 3px;"); }

        function successContent(value) { return customContent(value, "display: inline-block; background-color: springgreen; color: black; font-weight: bold; padding: 3px;"); }

        function alertContent(value) { return customContent(value, "display: inline-block; background-color: yellow; color: black; font-weight: bold; padding: 3px;"); }

        function warningContent(value) { return customContent(value, "display: inline-block; background-color: #c5050c; color: white; font-weight: bold; padding: 3px;"); }

        function customContent(value, format) {
            queue.push({ content: value, format: format })
            return token;
        }

        function fancyConsole(fn) {
            function fancyConsoleProxy() {
                if (!app.util.isDebugMode) return;

                var content = [];
                var formats = [];

                for (var i = 0; i < arguments.length; i++) {
                    var arg = arguments[i];

                    if (arg == token) {
                        var o = queue.shift();

                        content.push("%c" + o.content);
                        formats.push(o.format);
                    }
                    else {
                        if (typeof (arg) === "object" || typeof (arg) === "function") {
                            content.push("%o");
                            formats.push(arg);
                        }
                        else {
                            content.push("%c" + arg);
                            formats.push("");
                        }
                    }

                    content.push("%c ");
                    formats.push("");
                }

                fn(content.join(""), ...formats);
            }

            return fancyConsoleProxy;
        }

        return {
            log: fancyConsole(console.log),
            warn: fancyConsole(console.warn),
            error: fancyConsole(console.error),
            trace: fancyConsole(console.trace),
            group: fancyConsole(console.group),
            groupCollapsed: fancyConsole(console.groupCollapsed),
            groupEnd: fancyConsole(console.groupEnd),

            calloutContent: calloutContent,
            alertContent: alertContent,
            warningContent: warningContent,
            successContent: successContent,
            customContent: customContent
        };
    })(),
    copyToClipboard: function (s) {
        if (window.clipboardData && window.clipboardData.setData) {
            // IE specific code path to prevent textarea being shown while dialog is visible.
            return clipboardData.setData("Text", s);
        } else if (document.queryCommandSupported && document.queryCommandSupported("copy")) {
            var textarea = document.createElement("textarea");
            textarea.textContent = s;
            textarea.style.position = "fixed";  // Prevent scrolling to bottom of page in MS Edge.
            document.body.appendChild(textarea);
            textarea.select();
            try {
                return document.execCommand("copy");  // Security exception may be thrown by some browsers.
            } catch (ex) {
                console.warn("Copy to clipboard failed.", ex);
                return false;
            } finally {
                document.body.removeChild(textarea);
            }
        }
    },
    dateOrNull: function (s) {
        var d = new Date(s);
        return isNaN(d) ? null : d;
    },

    getElementValue: function (el, valueType) {
        if (el instanceof NodeList) {
            el = el[0];
        }
        switch (valueType) {
            case 'html':
                return el.outerHTML;
            case 'seltext':
                return el.options[el.selectedIndex].text;
            case 'val':
                return el.value;
            default:
                return el.value;
        }
    },
    getFormValues: function (el) {
        var unindexed_array = el.serializeArray();
        var result = {};
        for (var i = 0; i < unindexed_array.length; i++) {
            var item = unindexed_array[i];

            if (result[item.name]) {
                if (!Array.isArray(result[item.name])) {
                    result[item.name] = [result[item.name]];
                }

                result[item.name].push(item.value);
            } else {
                result[item.name] = item.value;
            }
        }

        return result;
    },
    getGuid: function () {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    },
    getIndexByPropertyValue: function (array, property, value) {
        var r = -1;
        array.some(function (item, i) {
            if (item[property] === value) {
                r = i;
                return true;
            }
            return false;
        });
        return r;
    },
    getPageSize: function () {
        var w = $(window).width();
        if (w < 768) {
            return 0;
        }
        else if (w <= 992) {
            return 1;
        }
        else if (w <= 1200) {
            return 3;
        }
        return 4;
    },
    getFormattedNumber: function (value, prepend) {
        if (value < 0) {
            prepend = "-" + (prepend ? prepend : "");
            value = Math.abs(value);
        }
        return (prepend ? prepend : "") + parseFloat(value).toLocaleString();
    },
    getAccountingNumber: function (value, prepend, append) {
        if (value < 0) {
            prepend = "(" + (prepend ? prepend : "");
            append = (append ? append : "") + ")";
            value = Math.abs(value);
        }
        return (prepend ? prepend : "") + parseFloat(value).toLocaleString(undefined, { 'minimumFractionDigits': 2, 'maximumFractionDigits': 2 }) + (append ? append : "");
    },
    getShortFriendlyNumber: function (value, prepend) {
        var format;
        var numberFormats = [
            { s: "T", v: 1000000000000, d: 0 },
            { s: "B", v: 1000000000, d: 0 },
            { s: "M", v: 1000000, d: 0 },
            { s: "k", v: 1000, d: 0 }
        ];

        if (value < 0) {
            prepend = "-" + (prepend ? prepend : "");
            value = Math.abs(value);
        }

        for (var i = 0; i < numberFormats.length; i++) {
            if (value >= numberFormats[i].v) {
                format = numberFormats[i];
                value = (value / format.v).toFixed(format.d);
                break;
            }
        }

        if (format) { value = value + format.s; }
        value = (prepend ? prepend : "") + value;

        return value;
    },
    getFriendlyNumber: function (value, prepend) {
        var format;
        var numberFormats = [
            { s: "T", v: 1000000000000, d: 2 },
            { s: "B", v: 1000000000, d: 2 },
            { s: "M", v: 1000000, d: 2 },
            { s: "k", v: 1000, d: 2 }
        ];

        if (value < 0) {
            prepend = "-" + (prepend ? prepend : "");
            value = Math.abs(value);
        }

        for (var i = 0; i < numberFormats.length; i++) {
            if (value >= numberFormats[i].v) {
                format = numberFormats[i];
                value = (value / format.v).toFixed(format.d);
                break;
            }
        }

        if (format) { value = value + format.s; }
        value = (prepend ? prepend : "") + value;

        return value;
    },

    isDebugMode: true,
    isJsonString: function (s) {
        try {
            const parsed = JSON.parse(s);
            return typeof parsed === 'object' && parsed !== null;
        } catch (e) {
            return false;
        }
    },
    isPageSmall: function () { return this.getPageSize() === 0; },
    isPageMedium: function () { return this.getPageSize() === 1; },
    isPageLarge: function () { return this.getPageSize() === 2; },
    isPageExtraLarge: function () { return this.getPageSize() === 3; },
    isPrintableCharacter: function (event) {
        //special cases

        //We do not define space as a printable character
        if (event.key === " ")
            return false;

        //As backspace will change the value, we define it as printable
        if (event.key === "Backspace")
            return true;
        //per the specs- printable characters will return single character if it is a printable. 
        //If it is a control, it will be longer than 1 character. 
        //If it is dead or unknown, it will be a defined string ("Dead" or "Unidentified")

        return event.key.length === 1;
        //return s === 8 || (s > 47 && s < 58) || (s > 64 && s < 91) || (s > 95 && s < 112) || (s > 185 && s < 193) || (s > 218 && s < 223);
    },

    merge: function (left, right, target) {
        var result = [];

        while (left.length && right.length) {
            //if (left[0] <= right[0]) {
            if ($('[data-label="' + target + '"]', left[0]).text() <=
                $('[data-label="' + target + '"]', right[0]).text()) {
                result.push(left.shift());
            } else {
                result.push(right.shift());
            }
        }

        if (this.isDebugMode) {
            app.util.console.groupCollapsed("left: " + left.length);
            app.util.console.groupEnd(" right: " + right.length);
        }

        while (left.length) result.push(left.shift());
        while (right.length) result.push(right.shift());

        return result;
    },
    mergeSort: function (arr, target) {
        if (arr.length < 2) return arr;

        var middle = parseInt(arr.length / 2);
        var left = arr.slice(0, middle);
        var right = arr.slice(middle, arr.length);

        return this.merge(this.mergeSort(left, target), this.mergeSort(right, target), target);
    },

    getCustomData: function (el) {
        customRecordData = {};
        if (el.data('component-key') && el.data('component-value') !== 'undefined') {
            var count = 2;


            customRecordData[el.data('component-key')] = el.data('component-value');

            while (el.data('component-key' + count) && el.data('component-value' + count)) {
                customRecordData[el.data('component-key' + count)] = el.data('component-value' + count);
                count++;
            }
        }
        return customRecordData;
    },

    // Helper function to wait until there are no pending requests
    waitForPendingRequests(form, callback) {

        form.removeData("validator");
        $.validator.unobtrusive.parse(form);

        var maxChecks = 50; // Maximum number of checks (adjust as needed)
        var checkCount = 0;

        var interval = setInterval(function () {
            if (form.valid() && form.data('validator').pendingRequest === 0) {
                clearInterval(interval);
                callback();
            } else if (!form.valid() || checkCount >= maxChecks) {
                clearInterval(interval);
            }
            checkCount++;
        }, 100); // Check every 100 milliseconds
    },

    getElementValue(el) {
        if (el.is(':checkbox') || el.is(':radio')) {
            return el.prop('checked');
        } else if (el.is('select')) {
            return el.find('option:selected').val();
        } else {
            return el.val();
        }
    },

    setVisibility(e, show) {
        if (show) {
            $(e).slideDown();
            $('input, select, textarea', e).prop("disabled", false);
        } else {
            $(e).slideUp();
            $('input, select, textarea', e).prop("disabled", true);
        }
    }
};

$(function () {
    app.page = new Page(window.applicationPath);
    $(document).trigger('page-init-complete');
});
